import { useCallback } from 'react'
import { Dayjs } from 'dayjs'
import FileSaver from 'file-saver'
import { useSnackbar } from 'notistack'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAccessToken } from '@/redux/reducers/auth.reducer'
import { getEnv } from '@/utils/helpers'

type ExportParams = {
  from: Dayjs
  to: Dayjs
  staffId?: string | null
  networkId?: string
}

export function useTimeTrackingExport({ from, to, staffId, networkId }: ExportParams) {
  const { enqueueSnackbar } = useSnackbar()
  const accessToken = useAppSelector(selectAccessToken)

  const handleExport = useCallback(async () => {
    if (!accessToken) return
    try {
      const baseUrl = getEnv('VITE_API_URL') || 'http://localhost:3005'
      const params = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() })
      if (staffId) params.set('staff', staffId)
      if (networkId) params.set('network', networkId)

      const response = await fetch(`${baseUrl}/time-tracking/sessions/export?${params}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!response.ok) throw new Error(`Export failed: ${response.status}`)

      const csvText = await response.text()
      const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' })
      const fileName = `time-tracking-${from.format('YYYY-MM-DD')}-${to.format('YYYY-MM-DD')}.csv`
      FileSaver.saveAs(blob, fileName)
    } catch (err) {
      console.error('Export error:', err)
      enqueueSnackbar('Export failed. Please try again.', { variant: 'error' })
    }
  }, [accessToken, from, to, staffId, networkId, enqueueSnackbar])

  return handleExport
}
